import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { DEFAULT_PAGING, OrganizationService } from 'src/app/@shared';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { PrintAttributeDefinition } from '../models/print-attribute.model';

/** Same filter shape as {@link EventPrintAttributeService} list filters. */
export interface EventAssociatedPrintAttributeListFilters {
  divisionIds: string[];
  attributeTypes: string[];
  eventTypeIds: string[];
}

const EMPTY_LIST_FILTERS: EventAssociatedPrintAttributeListFilters = {
  divisionIds: [],
  attributeTypes: [],
  eventTypeIds: [],
};

@Injectable({
  providedIn: 'root',
})
export class EventAssociatedPrintAttributeService {
  private loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private pageBehaviorSubject = new BehaviorSubject({
    ...DEFAULT_PAGING,
    pageSize: 20,
  });
  private sortBehaviorSubject = new BehaviorSubject({
    active: 'Name',
    direction: 'asc' as 'asc' | 'desc',
  });
  private searchBehaviorSubject = new BehaviorSubject<string>('');
  private reloadBehaviorSubject = new BehaviorSubject<string>('');
  private eventIdBehaviorSubject = new BehaviorSubject<string>('0');
  private listFiltersBehaviorSubject =
    new BehaviorSubject<EventAssociatedPrintAttributeListFilters>({
      ...EMPTY_LIST_FILTERS,
    });

  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public sort$ = this.sortBehaviorSubject.asObservable();
  public page$ = this.pageBehaviorSubject.asObservable();
  public search$ = this.searchBehaviorSubject.asObservable();
  public reload$ = this.reloadBehaviorSubject.asObservable();
  public listFilters$ = this.listFiltersBehaviorSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private organizationService: OrganizationService,
  ) {}

  setEventId(eventId: string): void {
    if (this.eventIdBehaviorSubject.value === eventId) {
      return;
    }
    this.eventIdBehaviorSubject.next(eventId);
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  setListFilters(filters: EventAssociatedPrintAttributeListFilters): void {
    this.listFiltersBehaviorSubject.next(filters);
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  clearListFilters(): void {
    this.setListFilters({ ...EMPTY_LIST_FILTERS });
  }

  private sortOrderByPath(active: string): string | null {
    const sortMap: Record<string, string> = {
      Name: 'Detail/PrintAttributeName',
      PrintAttributeType: 'Detail/PrintAttributeType',
      EventTypeNames: 'EventTypeNames',
      Description: 'Detail/Description',
    };
    return sortMap[active] ?? null;
  }

  private buildODataOrderBy(sort: { active: string; direction: string }): string | null {
    const path = this.sortOrderByPath(sort.active);
    if (!path) {
      return null;
    }
    const direction =
      String(sort.direction || 'asc').toLowerCase() === 'desc' ? 'desc' : 'asc';
    return `${path} ${direction}`;
  }

  public params$ = combineLatest([
    this.pageBehaviorSubject.pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
    this.listFiltersBehaviorSubject,
    this.eventIdBehaviorSubject,
  ]).pipe(
    distinctUntilChanged(
      (previous, current) => JSON.stringify(previous) === JSON.stringify(current),
    ),
    map(([page, sort, search, _reload, listFilters, eventId]) => {
      // Match {@link EventPrintAttributeService.params$} / GetByEventId query shape for GetAllByEvent.
      let params: HttpParams = new HttpParams({
        fromObject: {
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $count: true,
          $expand: 'Detail',
        },
      });

      const orderBy = this.buildODataOrderBy(sort);
      if (orderBy) {
        params = params.set('$orderby', orderBy);
      }

      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      if (eventId && eventId !== '0') {
        params = params.append('eventId', eventId);
      }

      listFilters.divisionIds.forEach((id) => {
        params = params.append('divisionIds', id);
      });
      listFilters.attributeTypes.forEach((type) => {
        params = params.append('attributeTypes', type);
      });
      listFilters.eventTypeIds.forEach((id) => {
        params = params.append('eventTypeIds', id);
      });

      return params;
    }),
  );

  sort(sort: any): void {
    this.sortBehaviorSubject.next(sort);
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  reload(): void {
    this.reloadBehaviorSubject.next(uuidv4());
  }

  page(page: any): void {
    this.pageBehaviorSubject.next(page);
  }

  search(search: string): void {
    this.searchBehaviorSubject.next(search);
    const paging = JSON.parse(JSON.stringify(DEFAULT_PAGING));
    paging.pageSize = 20;
    this.pageBehaviorSubject.next(paging);
  }

  private associatedResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)),
    switchMap((params) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/GetAllByEvent`;
      return this.httpClient.get<any>(url, { params });
    }),
    tap(() => this.loadingBehaviorSubject.next(false)),
    // Without refCount, last response is replayed forever; leaving this screen does not refetch on return.
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public associatedPrintAttributes$: Observable<PrintAttributeDefinition[]> =
    this.associatedResponse$.pipe(
      map((res: any) => {
        if (Array.isArray(res?.value)) {
          return res.value;
        }
        if (Array.isArray(res)) {
          return res;
        }
        return [];
      }),
    );

  public totalRecords$: Observable<number> = this.associatedResponse$.pipe(
    map((res: any) => {
      if (typeof res?.['@odata.count'] === 'number') {
        return res['@odata.count'];
      }
      if (Array.isArray(res?.value)) {
        return res.value.length;
      }
      if (Array.isArray(res)) {
        return res.length;
      }
      return 0;
    }),
  );

  /**
   * Persists dynamic field values for a print attribute on an event.
   * `value` is sent as a JSON string query parameter (API contract).
   */
  createEventAssociation(
    eventId: string,
    printAttributeId: string,
    value: Record<string, string>,
  ): Observable<unknown> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PrintAttributeDomains/CreateEventAssociation`;
    const params = new HttpParams()
      .set('eventId', eventId)
      .set('printAttributeId', printAttributeId)
      .set('value', JSON.stringify(value));
    return this.httpClient.post(url, {}, { params });
  }
}
